'use client';

import { ensureClient } from './util';
import {
  detectInAppBrowser,
  hasGoogleOAuthIssues,
  redirectToExternalBrowser,
  showInAppBrowserWarning,
  getAppDisplayName,
} from '@/lib/browserDetection';

export interface GoogleUserInfo {
  id: string;
  email: string;
  name: string;
  avatar?: string;
}

/**
 * Callback URL for the register popup. Shared by both `getGoogleUserInfo`
 * branches so the two never drift, and built with `URL` rather than string
 * interpolation so escaping is the platform's job, not ours.
 */
function registerCallbackUrl(locale?: string): string {
  const url = new URL(`${window.location.origin}/auth/callback`);
  url.searchParams.set('mode', 'register');
  if (locale) url.searchParams.set('locale', locale);
  return url.toString();
}

export const authModel = {
  async getSession() {
    const client = ensureClient();
    const { data, error } = await client.auth.getSession();
    if (error) throw error;
    return data.session;
  },

  async requireSession() {
    const session = await this.getSession();
    if (!session) throw new Error('Not authenticated');
    return session;
  },

  async signInWithPassword(email: string, password: string) {
    const client = ensureClient();
    const { data, error } = await client.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  },

  /**
   * `locale` is sent separately from `redirectPath` on purpose: `redirectPath`
   * only exists when the login page was reached with a `?redirect=`, so signing
   * in from a bare `/ja-JP/login` would otherwise tell the callback nothing
   * about the user's locale and bring them back on en-AU.
   */
  async signInWithGoogle(redirectPath?: string, locale?: string) {
    const client = ensureClient();

    // Check for in-app browser issues before proceeding
    if (hasGoogleOAuthIssues()) {
      const browserInfo = detectInAppBrowser();
      const appName = getAppDisplayName(browserInfo.appName);

      // Show warning to user
      showInAppBrowserWarning();

      // Try to redirect to external browser
      const oauthUrl = `${window.location.origin}/auth/google-oauth`;
      if (redirectToExternalBrowser(oauthUrl)) {
        // Return early as we're redirecting
        return { url: oauthUrl };
      }

      // If redirect fails, throw an informative error
      throw new Error(
        `Google Sign-In may not work properly in ${appName}. Please try opening this page in your device's default browser (Safari/Chrome).`,
      );
    }

    // Build callback URL with redirect parameter if provided
    const callbackUrl = new URL(`${window.location.origin}/auth/callback`);
    if (redirectPath) {
      callbackUrl.searchParams.set('next', redirectPath);
    }
    if (locale) {
      callbackUrl.searchParams.set('locale', locale);
    }

    const { data, error } = await client.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: callbackUrl.toString(),
      },
    });
    if (error) throw error;
    return data;
  },

  async signOut() {
    const client = ensureClient();
    const { error } = await client.auth.signOut();
    if (error) throw error;
  },

  async sendOtpForPasswordReset(email: string, locale?: string) {
    const res = await fetch('/api/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, locale }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.error || 'Failed to send password reset email');
    }
  },

  async verifyOtpForPasswordReset(email: string, token: string) {
    const client = ensureClient();
    const { data, error } = await client.auth.verifyOtp({
      email,
      token,
      type: 'recovery',
    });
    if (error) throw error;
    return data;
  },

  /**
   * Password reset (recovery session). Same behaviour as the app: `same_password`
   * means the reset is done (the player typed the password they already have), and
   * once it is set every other device is signed out; failing to do that last step
   * never turns a changed password into an error.
   */
  async updatePassword(newPassword: string): Promise<'updated' | 'already_current'> {
    const client = ensureClient();
    let result: 'updated' | 'already_current' = 'updated';
    const { error } = await client.auth.updateUser({ password: newPassword });
    if (error) {
      if ((error as { code?: string }).code !== 'same_password') throw error;
      result = 'already_current';
    }
    try {
      const { error: signOutError } = await client.auth.signOut({ scope: 'others' });
      if (signOutError) {
        console.error('password reset: sign out other sessions failed', signOutError);
      }
    } catch (e) {
      console.error('password reset: sign out other sessions failed', e);
    }
    return result;
  },

  async changePassword(currentPassword: string, newPassword: string) {
    const client = ensureClient();

    // Get current user
    const {
      data: { user },
      error: userError,
    } = await client.auth.getUser();
    if (userError || !user) throw new Error('Not authenticated');

    // Verify current password by attempting to sign in
    const { error: verifyError } = await client.auth.signInWithPassword({
      email: user.email!,
      password: currentPassword,
    });

    if (verifyError) {
      throw new Error('Current password is incorrect');
    }

    // If verification successful, update to new password
    const { error: updateError } = await client.auth.updateUser({
      password: newPassword,
    });

    if (updateError) throw updateError;
  },

  /**
   * `locale` rides along so the popup's *error* path stays in the user's
   * language: the happy path returns self-closing HTML before any redirect,
   * but a failed code exchange falls through to `/{locale}/auth/auth-code-error`.
   */
  async getGoogleUserInfo(locale?: string): Promise<GoogleUserInfo> {
    const client = ensureClient();

    // Check for in-app browser issues before proceeding
    if (hasGoogleOAuthIssues()) {
      const browserInfo = detectInAppBrowser();
      const appName = getAppDisplayName(browserInfo.appName);

      // Show warning to user
      showInAppBrowserWarning();

      // For popup-based OAuth, we need to handle this differently
      // Try to redirect to external browser for the OAuth URL
      const { data, error } = await client.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: registerCallbackUrl(locale),
          queryParams: { access_type: 'offline', prompt: 'consent' },
          skipBrowserRedirect: true,
        },
      });
      if (error) throw error;

      // Try to redirect to external browser
      if (redirectToExternalBrowser(data.url)) {
        // Wait a bit and then check for session
        return new Promise((resolve, reject) => {
          const checkSession = setInterval(async () => {
            try {
              const { data: sessionData } = await client.auth.getSession();
              if (sessionData.session?.user) {
                clearInterval(checkSession);
                const user = sessionData.session.user;
                resolve({
                  id: user.id,
                  email: user.email || '',
                  name: user.user_metadata?.full_name || user.user_metadata?.name || '',
                  avatar: user.user_metadata?.avatar_url || user.user_metadata?.picture,
                });
              }
            } catch {
              // Continue checking
            }
          }, 2000);

          // Timeout after 2 minutes
          setTimeout(() => {
            clearInterval(checkSession);
            reject(
              new Error(
                `OAuth timeout. Please complete the sign-in process in your default browser.`,
              ),
            );
          }, 120000);
        });
      }

      // If redirect fails, throw an informative error
      throw new Error(
        `Google Sign-In may not work properly in ${appName}. Please try opening this page in your device's default browser (Safari/Chrome).`,
      );
    }

    // Standard popup flow for regular browsers
    const { data, error } = await client.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: registerCallbackUrl(locale),
        queryParams: { access_type: 'offline', prompt: 'consent' },
        skipBrowserRedirect: true,
      },
    });
    if (error) throw error;

    const popup = window.open(
      data.url,
      'google-oauth',
      'width=500,height=600,scrollbars=yes,resizable=yes',
    );
    if (!popup) throw new Error('Popup blocked. Please allow popups for this site.');

    return new Promise((resolve, reject) => {
      const checkClosed = setInterval(async () => {
        try {
          if (popup.closed) {
            clearInterval(checkClosed);
            const { data: sessionData } = await client.auth.getSession();

            if (sessionData.session?.user) {
              const user = sessionData.session.user;
              resolve({
                id: user.id,
                email: user.email || '',
                name: user.user_metadata?.full_name || user.user_metadata?.name || '',
                avatar: user.user_metadata?.avatar_url || user.user_metadata?.picture,
              });
            } else {
              reject(new Error('OAuth cancelled or failed'));
            }
          }
        } catch {
          // Handle COOP policy blocking popup.closed access
          clearInterval(checkClosed);
          try {
            const { data: sessionData } = await client.auth.getSession();
            if (sessionData.session?.user) {
              const user = sessionData.session.user;
              resolve({
                id: user.id,
                email: user.email || '',
                name: user.user_metadata?.full_name || user.user_metadata?.name || '',
                avatar: user.user_metadata?.avatar_url || user.user_metadata?.picture,
              });
            } else {
              reject(new Error('OAuth cancelled or failed'));
            }
          } catch (sessionError) {
            reject(sessionError);
          }
        }
      }, 1000);

      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(checkClosed);
        reject(new Error('OAuth timeout'));
      }, 300000);
    });
  },
};
